// @flow
import React from 'react';
import {Box, Typography, makeStyles} from '@material-ui/core';

import {
  Delete,
  DragIndicator as DragIcon,
  Image as ImageIcon,
  InfoTwoTone,
} from '@material-ui/icons';
import {SortableItem, SortableKnob} from 'react-easy-sort';
import {colors} from '../../../../../assets/styles/colors';

import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import {getImageUrl} from '../../../../../utils/image_provider';

import type {ProductOptionGroupOptionConfig} from '../../../../../entities/product';
import {useStoreOptionActions} from './OptionsContext';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {TiendadaSwitch} from '../../../../../elements/tiendada-switch';
import TiendadaTooltip from '../../../../../elements/tiendada-tooltip';

const useStyles = makeStyles(theme => ({
  optionItem: {
    display: 'grid',
    alignItems: 'center',
    borderBottom: `1px solid ${colors.grey400}`,
    backgroundColor: colors.white,
    paddingBlock: '.5rem',
    cursor: 'pointer',
    '&:last-child': {
      borderBottom: 'none',
    },
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: '24px 1fr',
      cursor: 'default',
    },
  },
  optionDragHandle: {
    cursor: 'grab',
    color: colors.grey400,
    height: '100%',
    display: 'none',

    '&:active': {
      cursor: 'grabbing',
    },

    '&:hover': {
      color: colors.primary,
    },
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
  },

  optionImage: {
    width: 36,
    height: 36,
    flexShrink: 0,
    borderRadius: '.5rem',
    objectFit: 'cover',
    border: `1px solid ${colors.grey300}`,

    [theme.breakpoints.up('sm')]: {
      width: 40,
      height: 40,
    },
  },
  colorDot: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    flexShrink: 0,
    border: `2px solid ${colors.grey300}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    [theme.breakpoints.up('sm')]: {
      width: 40,
      height: 40,
    },
  },
  optionItemName: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey800,
    '-webkit-line-clamp': 2,
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    flexGrow: 1,
  },
  rootIosSwitch: {
    margin: 0,
    '&:disabled': {
      opacity: 0.5,
    },
  },
  configContainer: {
    display: 'flex',
    gap: '.5rem',
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  priceInput: {
    width: 80,
    '& .MuiInputBase-input': {
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      padding: '.5rem !important',
    },
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  statusChip: {
    height: 24,
    fontSize: '0.75rem',
    minWidth: 60,
  },
  activeChip: {
    backgroundColor: colors.green100,
    color: colors.green800,
  },
  inactiveChip: {
    backgroundColor: colors.grey200,
    color: colors.grey700,
  },
  defaultChip: {
    backgroundColor: colors.blue100,
    color: colors.blue800,
  },
  configToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  configLabel: {
    fontSize: '0.75rem',
    color: colors.grey600,
    margin: 0,
  },
  helpText: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: colors.grey600,
    fontStyle: 'italic',
    marginTop: '.25rem',
  },
  optionDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',

    [theme.breakpoints.up('md')]: {
      display: 'grid',
      gridTemplateColumns: '1.5fr 2fr',
    },
  },
  optionInfoOption: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '.5rem',
    padding: '.5rem .25rem .5rem 0',
    cursor: 'auto',
  },
  removeOption: {
    color: colors.danger,
    fontSize: '0.75rem',
    border: `1px solid ${colors.danger}`,
    padding: '2px 12px',
  },

  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: '.6rem',
    fontWeight: 600,
    padding: '4px 8px',
    borderRadius: 999,
    border: `1px solid ${colors.grey300}`,
    background: 'rgba(0,0,0,0.03)',

    '&[data-config="global"]': {
      borderColor: '#3b82f6',
    },
    '&[data-config="local"]': {
      borderColor: '#31c48d',
    },
  },
  badgeDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    '&[data-config="global"]': {
      backgroundColor: '#3b82f6',
    },
    '&[data-config="local"]': {
      backgroundColor: '#31c48d',
    },
  },
}));

type Props = {
  option: ProductOptionGroupOptionConfig,
  groupId: string,
};

function OptionItem({option, groupId}: Props) {
  const classes = useStyles();

  const {updateOptionInGroups, removeOption} = useStoreOptionActions();

  const storeOption = option.storeProductOption;

  const defaultOptionPrice = option.storeProductOption.price;
  const defaultOptionEnabled = option.storeProductOption.enabled;

  const isUsingDefault = option.useDefault;
  const customOptionPrice = option.price ?? defaultOptionPrice;
  const customOptionEnabled = option.enabled ?? defaultOptionEnabled;

  const optionPrice = isUsingDefault ? defaultOptionPrice : customOptionPrice;
  const optionsEnabled = isUsingDefault
    ? defaultOptionEnabled
    : customOptionEnabled;

  const handleUseDefaultChange = useDefault => {
    updateOptionInGroups(groupId, option.id, {useDefault});
  };

  const handlePriceChange = newPrice => {
    updateOptionInGroups(groupId, option.id, {
      price: parseFloat(newPrice) || 0,
    });
  };

  const handleEnabledChange = enabled => {
    updateOptionInGroups(groupId, option.id, {
      enabled,
    });
  };

  const imageUrl = storeOption.imageId
    ? getImageUrl(storeOption.imageId)
    : undefined;

  const handleRemoveOption = () => {
    removeOption(groupId, option.id);
  };

  return (
    <SortableItem key={option.id}>
      <Box className={classes.optionItem}>
        <SortableKnob>
          <span className={classes.optionDragHandle}>
            <DragIcon fontSize="small" />
          </span>
        </SortableKnob>
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          style={{gap: '.5rem'}}
        >
          <Box className={classes.optionDetails}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexGrow="1"
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                flexGrow="1"
                style={{gap: '.5rem'}}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={storeOption.name}
                    className={classes.optionImage}
                  />
                ) : storeOption.color ? (
                  <Box
                    className={classes.colorDot}
                    style={{backgroundColor: storeOption.color}}
                  />
                ) : (
                  <Box className={classes.colorDot}>
                    <ImageIcon fontSize="small" />
                  </Box>
                )}
                <Box
                  display="flex"
                  alignItems="center"
                  style={{gap: '.5rem'}}
                  flexGrow="1"
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    flexGrow="1"
                    alignItems="flex-start"
                  >
                    <Typography className={classes.optionItemName}>
                      {storeOption.name}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{gap: '.5rem'}}
                  >
                    <Typography variant="caption">Precio</Typography>
                    <CustomTextField
                      className={classes.priceInput}
                      size="small"
                      disabled={isUsingDefault}
                      value={optionPrice}
                      type="number"
                      inputProps={{
                        min: 0,
                      }}
                      onChange={e => handlePriceChange(e.target.value)}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              justifyContent="space-between"
              style={{
                gap: '.5rem',
              }}
            >
              <Box
                display="flex"
                style={{gap: '.5rem'}}
                alignItems="center"
                flexGrow="1"
              >
                <TiendadaSwitch
                  label="Usar valores predeterminados"
                  size="small"
                  checked={isUsingDefault}
                  onChange={e => {
                    handleUseDefaultChange(e.target.checked);
                  }}
                  color="primary"
                  classes={{root: classes.rootIosSwitch}}
                />
                {isUsingDefault && (
                  <TiendadaTooltip
                    title="Valores tomados desde la configuración de opciones de la tienda."
                    placement="top"
                  >
                    <InfoTwoTone
                      style={{
                        color: colors.info,
                      }}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    />
                  </TiendadaTooltip>
                )}
              </Box>
              <Box
                display="flex"
                style={{gap: '.5rem'}}
                alignItems="center"
                component="label"
              >
                <TiendadaSwitch
                  checked={optionsEnabled}
                  onChange={e => handleEnabledChange(e.target.checked)}
                  color="primary"
                  size="small"
                  disabled={isUsingDefault}
                  classes={{root: classes.rootIosSwitch}}
                  label={optionsEnabled ? 'Activo' : 'Inactivo'}
                />
              </Box>
              <CustomButton
                startIcon={<Delete fontSize="small" />}
                onClick={e => {
                  e.stopPropagation();
                  handleRemoveOption();
                }}
                className={classes.removeOption}
              >
                Eliminar
              </CustomButton>
            </Box>
          </Box>
          {/*     <Box>
            <span
              className={classes.badge}
              title={
                isUsingDefault
                  ? 'Se está usando la configuración por defecto de grupo'
                  : 'Se está usando la configuración local'
              }
              data-config={isUsingDefault ? 'global' : 'local'}
            >
              <span
                className={classes.badgeDot}
                data-config={isUsingDefault ? 'global' : 'local'}
              />
              <span>
                {isUsingDefault
                  ? 'Configuración por defecto'
                  : 'Configuración local'}
              </span>
            </span>
          </Box> */}
        </Box>
      </Box>
    </SortableItem>
  );
}

const MemoOptionItem = React.memo(OptionItem);

export default MemoOptionItem;
